import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch } from 'react-icons/fa';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from './ConfirmationModal';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import authHeader from "../services/auth-header";
import { API_URL_AUTH, API_URL_ROLES } from '../constants/index';
import { RootState } from '../store';
import { IUser, IRole } from "../interfaces/types";

interface IFormValues {
  name: string;
  lastname: string;
  username: string;
  email: string;
  phone: string;
  roles: IRole[];
}

function Users() {
  const [users, setUsers] = useState<IUser[]>([]);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<IUser | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState('');
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const { user: loggedUser } = useSelector((state: RootState) => state.auth);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [serverError, setServerError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const userSchema = Yup.object().shape({
    name: Yup.string().required('Nombre es obligatorio'),
    lastname: Yup.string().required('Apellido es obligatorio'),
    username: Yup.string().required('Nombre de usuario es obligatorio'),
    email: Yup.string().email('Email no válido').required('Email es obligatorio'),
    phone: Yup.string()
      .matches(/^\d{8,11}$/, 'Número de teléfono debe tener entre 8 y 11 dígitos y contener sólo números'),
    roles: Yup.array().min(1, 'Al menos un role es requerido')
  });

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL_AUTH}/users`, {
        params: {
          keyword: searchTerm + '',
          page: currentPage - 1,
          size: 10
        },
        headers: authHeader()
      });
      if (response.data.content) {
        setUsers(response.data.content);
        setTotalPages(response.data.totalPages);
      } else {
        setUsers([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error buscando usuarios:', error);
    }
  }, [currentPage, searchTerm]);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axios.get(API_URL_ROLES, { headers: authHeader() });
      const rolesOptions = response.data.content.map((role: IRole) => ({
        id: role.id,
        name: role.name
      }));
      setRoles(rolesOptions);
    } catch (error) {
      console.error('Error buscando roles:', error);
    }
  }, []);

  useEffect(() => {
    if (loggedUser) {
      fetchUsers();
      fetchRoles();
    } else {
      navigate('/login');
    }
  }, [loggedUser, navigate, fetchUsers, fetchRoles]);

  const handleRoleChange = (selectedOptions: IRole[], setFieldValue: (field: string, value: any) => void) => {
    const rolesWithNames = selectedOptions.map(option => ({
      id: option.id,
      name: option.name
    }));
    setFieldValue('roles', rolesWithNames);
  };

  const handleShowModal = (user: IUser | null = null) => {
    setCurrentUser(user);
    setServerError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentUser(null);
  };

  const handleDelete = async (userId: number) => {
    try {
      await axios.delete(`${API_URL_AUTH}/users/${userId}`, { headers: authHeader() });
      fetchUsers();
    } catch (error) {
      console.error('Error eliminando usuario:', error);
    } finally {
      setCurrentPage(1);
    }
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTempSearchTerm(event.target.value);
  };

  const handleSearchClick = () => {
    setSearchTerm(tempSearchTerm);
    setCurrentPage(1);
  };

  const handleOpenConfirmationModal = (userId: number) => {
    setSelectedUserId(userId);
    setModalOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleConfirmAction = () => {
    if (selectedUserId !== null) {
      handleDelete(selectedUserId);
      setSelectedUserId(null);
      setModalOpen(false);
    }
  };

  const handleCloseConfirmationModal = () => {
    setSelectedUserId(null);
    setModalOpen(false);
  };

  const handleUserSubmit = async (values: IFormValues, actions: FormikHelpers<IFormValues>) => {
    const endpoint = currentUser ? `${API_URL_AUTH}/users/${currentUser.id}` : `${API_URL_AUTH}/signup`;
    const method = currentUser ? 'put' : 'post';
    try {
      await axios[method](endpoint, values, { headers: authHeader() });
      fetchUsers();
      handleCloseModal();
      setCurrentPage(1);
    } catch (error: any) {
      console.error('Error guardando usuario:', error);
      setServerError(error.response?.data?.message || 'Error al guardar usuario');
      actions.setSubmitting(false);
    }
  };

  return (
    <Container>
      <div className='row page-title mt-3 mb-3'>
        <h1>Usuarios</h1>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <OverlayTrigger placement="top" overlay={<Tooltip id="search-tooltip">Busca por nombre de usuario</Tooltip>}>
          <InputGroup className="me-2" style={{ width: "auto" }}>
            <Form.Control
              type="text"
              placeholder="Buscar usuario..."
              value={tempSearchTerm}
              onChange={handleSearchInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearchClick();
                }
              }}
              className='search-input'
            />
            <InputGroup.Text>
              <Button variant="primary" onClick={handleSearchClick}><FaSearch /></Button>
            </InputGroup.Text>
          </InputGroup>
        </OverlayTrigger>
        <Button className="button-add-container" variant="primary" onClick={() => handleShowModal()}>
          <FaPlus className='me-2' /> Usuario
        </Button>
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>ID</th>
            <th>Nombre del usuario</th>
            <th>Nombre de usuario</th>
            <th>Email</th>
            <th>Roles</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.id}>
              <td>{user.id}</td>
              <td>{user.name} {user.lastname}</td>
              <td>{user.username}</td>
              <td>{user.email}</td>
              <td>{user.roles.map(item => `${item.name}(${item.id})`).join(', ')}</td>
              <td>
                <Button variant="primary" onClick={() => handleShowModal(user)} className="me-2 custom-primary-btn"><FaEdit /></Button>
                <Button variant="danger" onClick={() => handleOpenConfirmationModal(user.id)} className='row-delete-button btn-delete'><FaTrash /></Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {totalPages > 1 && (
        <nav>
          <ul className="pagination">
            {Array.from({ length: totalPages }, (_, index) => (
              <li key={index} className={`page-item page-index ${currentPage === index + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => handlePageChange(index + 1)}>{index + 1}</button>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{currentUser ? 'Editar Usuario' : 'Crear Usuario'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(serverError && serverError !== '') && (<div className="alert alert-info">{serverError}</div>)}
          <Formik
            initialValues={{
              name: currentUser?.name || '',
              lastname: currentUser?.lastname || '',
              username: currentUser?.username || '',
              email: currentUser?.email || '',
              phone: currentUser?.phone || '',
              roles: currentUser?.roles || []
            }}
            validationSchema={userSchema}
            onSubmit={handleUserSubmit}
            enableReinitialize
          >
            {({ handleSubmit, handleChange, setFieldValue, setFieldTouched, values, touched, errors }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre</Form.Label>
                  <Form.Control
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    isInvalid={!!touched.name && !!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Apellidos</Form.Label>
                  <Form.Control
                    name="lastname"
                    value={values.lastname}
                    onChange={handleChange}
                    isInvalid={!!touched.lastname && !!errors.lastname}
                  />
                  <Form.Control.Feedback type="invalid">{errors.lastname}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre de usuario</Form.Label>
                  <Form.Control
                    name="username"
                    value={values.username}
                    onChange={handleChange}
                    isInvalid={!!touched.username && !!errors.username}
                  />
                  <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    isInvalid={!!touched.email && !!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Teléfono</Form.Label>
                  <Form.Control
                    name="phone"
                    value={values.phone}
                    onChange={handleChange}
                    isInvalid={!!touched.phone && !!errors.phone}
                  />
                  <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="roles">
                  <Form.Label>Roles</Form.Label>
                  <Select
                    isMulti
                    name="roles"
                    options={roles}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    value={values.roles}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => String(option.id)}
                    onChange={(selectedOptions) => {
                      handleRoleChange(selectedOptions as IRole[], setFieldValue);
                      setFieldTouched('roles', true, false);
                    }}
                  />
                  {!!touched.roles && !!errors.roles && (
                    <div className="text-danger mt-1">{errors.roles as string}</div>
                  )}
                </Form.Group>
                <Button className='btn-custom-primary' variant="primary" type="submit">
                  {currentUser ? 'Actualizar' : 'Crear'}
                </Button>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>

      <ConfirmationModal
        isOpen={modalOpen}
        message="¿Está seguro que desea eliminar el usuario?"
        onCancel={handleCloseConfirmationModal}
        onConfirm={handleConfirmAction}
        cancelText="Cancelar"
        confirmText="Confirmar"
      />
    </Container>
  );
}

export default Users;
